import api from "app/api";
import {
  CalendarEventItem as BaseCalendarEventItem,
  SaveFormProps,
} from "common";
import { BoatItem } from "features/boats/boatApi";
import { BookingItem } from "features/bookings/bookingApi";
import { ProgramItem } from "features/programs/programApi";
import {
  ScheduleItem,
  ScheduleLimit,
  ScheduleOption,
  ScheduleSeatingLayout,
  ScheduleVariant,
} from "features/schedules/scheduleApi";
import moment from "moment";
import { groupEditEvents } from "./Editor/eventEditorApi";
import { EVENT_STATUSES } from "./eventConstants";

export type EventVariant = {
  number_of_adults: number;
  number_of_kids: number;
  number_of_babies: number;
  number_of_guides: number;
  number_of_guests: number;
  number_of_places: number;
  closed: boolean;
  online_closed: boolean;
  remaining_places: number;
} & ScheduleVariant;

export type EventLimit = {
  remaining_online_places: number;
  remaining_places: number;
  reserved_places: number;
  number_of_guests: number;
  is_shared: boolean;
  over_tolerance: boolean;
  over_limit: boolean;
  online_over_tolerance: boolean;
  online_over_limit: boolean;
  closed: boolean | null;
  online_closed: boolean | null;
} & ScheduleLimit;

export type EventOption = {
  number_of_bookings: number;
} & ScheduleOption;

export type EventItem = {
  id: number;
  start_time: string;
  boat_id: number;
  program_id: number;
  schedule_id: number;
  schedule: ScheduleItem;
  duration: number;
  limits: EventLimit[];
  //limits_stat: EventLimit[];
  variants: EventVariant[];
  //variants_stat: EventVariant[];
  options: EventOption[];
  layouts?: ScheduleSeatingLayout[];
  summary: EventVariant;
  closed: number[] | null;
  online_closed: number[] | null;
  variant_closed_status?: 0 | 1 | null;
  custom: any;
  status: EVENT_STATUSES;
  private: boolean;
  created_by: number;
  updated_by: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  boat: BoatItem;
  program: ProgramItem;
  event_group_id: number;
  group_variant_ids: number[];
  boat_limit?: EventLimit;
  boat_closed?: boolean;
  boat_online_closed?: boolean;
  boat_low_cancelled?: boolean;
  number_of_should_notify?: number;
};

export type CalendarEventItem = EventItem & BaseCalendarEventItem;

export function prepareCalendarItems(items: EventItem[]): CalendarEventItem[] {
  let _items: CalendarEventItem[] = [];
  items.forEach(row => {
    _items.push({
      ...row,
      title: row?.program?.name,
      start: moment(row?.start_time),
      end: moment(row?.start_time).add(row?.duration, "minutes"),
      color: row.program?.color,
    });
  });

  return _items;
}

export function getEventService(scope?: any) {
  return api.getService<EventItem>("events", {
    scope,
    //params goes here
  });
}

export function getGroupEventService(scope?: any, eventIds?: any) {
  return {
    ...api.getService<EventItem>("events", {
      scope,
      //params goes here
    }),
    updateItem: async (
      id: number,
      item: Partial<EventItem>,
      onlyTrashed?: boolean
    ) => {
      const { success, error } = await groupEditEvents({
        ...item,
        event_ids: eventIds,
      });

      return { data: item, success, error };
    },
  };
}

export function getEventCalendarService(scope?: any) {
  return api.getService<EventItem>("events/calendar", {
    scope,
    //params goes here
  });
}

export async function updateEventAvailability(
  eventId: number,
  variant_ids: number[],
  close: boolean,
  online: boolean,
  boat?: boolean
): Promise<SaveFormProps<EventItem>> {
  const response = await api.post<EventItem>(
    `events/${eventId}/update-availability`,
    {
      variant_ids,
      online: online ? 1 : null,
      close: close ? 1 : null,
      boat: boat ? 1 : null,
    }
  );
  return response;
}
export async function getClosestEvent(
  programId: number,
  datetime: string
): Promise<EventItem> {
  const response = await api.get<EventItem>(
    `events/find-closest-event?program_id=${programId}&datetime=${datetime}`
  );
  return response.data;
}
export async function getClosestEvents(
  program_id: number,
  datetime: string
): Promise<EventItem[]> {
  const response = await api.get<EventItem[]>(`events/find-closest-events`, {
    params: { program_id, datetime },
  });
  return response.data;
}

export type MoveBookingsState = {
  movable: BookingItem[];
  overlimit: BookingItem[];
  event?: EventItem;
};

export function checkBookings(eventId: any, bookingIds?: any[]) {
  return api.get<MoveBookingsState>(
    `events/${eventId}/check-movable-bookings`,
    {
      params: { bookingIds },
    }
  );
}
export function moveBookings(
  eventId: any,
  bookings?: BookingItem[],
  force?: boolean
) {
  return api.post<MoveBookingsState>(`events/${eventId}/move-bookings`, {
    bookings,
    force,
  });
}
export function updateBoatLowCancelled(
  eventGroupId: any,
  lowCancelled?: boolean
) {
  return api.post<MoveBookingsState>(
    `event-groups/${eventGroupId}/low-cancelled`,
    {
      low_cancelled: lowCancelled,
    }
  );
}

export function cancelAllBookings(eventId: any) {
  return api.post<{ count: number }>(`events/${eventId}/cancel-bookings`);
}

export function cancelEventsByIds(eventIds?: any[]) {
  return api.post<{ count: number }>(`events/cancel`, {
    eventIds,
  });
}
